import { useState } from 'react'
import { ToteEngagementModeEnum } from '@mote/api'
import { Block, H5 } from '@mote/ui'
import { ActionButton, ContentWrapper, Root } from './ToteCardComponent.styles'
import { ACTION_DATA, SCREEN_MODES, formatLastSeenDate } from './helpers'
import { StundentTesponse, TeacherTesponse, Teply } from './views'

/**
 * @typedef {Object} OnReplyObject
 * @property {string} seenMeme
 * @property {ToteEngagementMode} mode
 * @property {string} teply
 */

/**
 * @typedef {Pick<ToteStatus, 'createdByAvatar' | 'createdByName', 'wasCreatedBySameUser', 'studentName', 'lastSeenEpoch', 'lastLoopMode', 'lastTeply'>} ToteStatusPick
 */
/**
 * @typedef {Object} ToteCardComponentProps
 * @property {ToteStatusPick} toteStatus
 * @property {boolean} isUnauthenticated
 * @property {boolean} isMarginTopVisible
 * @property {(obj: OnReplyObject) => void} onReply
 * @property {JSX.Element} children
 * @property {boolean} transparent
 * @property {(mode: ToteEngagementMode) => string} getImageForMode
 */

/**
 * @typedef {Object} ModeState
 * @property {ToteCardMode} mode
 * @property {ToteEngagementMode | null} currentFlowMode
 * @property {string | null} responseText
 * @property {boolean} shouldShowMeme
 */

/**
 * @param {ToteCardComponentProps} props
 * @return {JSX.Element}
 * @constructor
 */

export function ToteCardComponent(props) {
  /**
   * @type useState<ModeState>
   */
  const [screenState, setScreenState] = useState({
    mode: getInitialMode(),
    currentFlowMode: props.toteStatus.lastLoopMode,
    responseText: props.toteStatus.lastTeply,
    currentImage: ''
  })

  let content
  switch (screenState.mode) {
    case SCREEN_MODES.RESPONSE:
    case SCREEN_MODES.AWAITING: {
      const data = getData()
      if (data.isTeacherView) {
        content = (
          <TeacherTesponse
            {...data}
            image={screenState.currentImage}
            shouldShowInstructionLink={!props.toteStatus.lastLoopMode}
            shouldShowMeme={Boolean(screenState.currentImage)}
            lastSeenDate={formatLastSeenDate(props.toteStatus.lastSeenEpoch)}
          />
        )
      } else {
        content = (
          <StundentTesponse
            {...data}
            image={screenState.currentImage}
            shouldShowMeme={Boolean(screenState.currentImage)}
            lastSeenDate={formatLastSeenDate(props.toteStatus.lastSeenEpoch)}
          />
        )
      }
      break
    }
    case SCREEN_MODES.TEPLY: {
      const data = getData()
      content = (
        <Teply
          data={data}
          image={screenState.currentImage}
          mode={screenState.currentFlowMode}
          shouldShowMeme={Boolean(screenState.currentImage)}
        />
      )
      break
    }
    default: {
      content = (
        <ContentWrapper className='mote-tote-card__content' gap={16}>
          <H5 fontSize={14} fontWeight={500} lineHeight='120%'>
            Respond to {props.toteStatus.createdByName}:
          </H5>
          <Block
            className='mote-tote-card__action-buttons'
            layout={'vertical'}
            gap={10}
            spreadX
          >
            <ActionButton
              icon={ACTION_DATA[ToteEngagementModeEnum.AFFIRMATIVE].icon}
              aria-label={ACTION_DATA[ToteEngagementModeEnum.AFFIRMATIVE].text}
              text={ACTION_DATA[ToteEngagementModeEnum.AFFIRMATIVE].text}
              onClick={() =>
                handleModeChange(ToteEngagementModeEnum.AFFIRMATIVE)
              }
            />
            <ActionButton
              icon={ACTION_DATA[ToteEngagementModeEnum.ASSISTIVE].icon}
              aria-label={ACTION_DATA[ToteEngagementModeEnum.ASSISTIVE].text}
              text={ACTION_DATA[ToteEngagementModeEnum.ASSISTIVE].text}
              onClick={() => handleModeChange(ToteEngagementModeEnum.ASSISTIVE)}
            />
          </Block>
        </ContentWrapper>
      )
    }
  }

  const children = getChildren()
  return (
    <Root
      className={props.className}
      isMarginTopVisible={props.isMarginTopVisible}
    >
      {children}
      {content}
    </Root>
  )

  function getInitialMode() {
    if (props.toteStatus.lastLoopMode) {
      return SCREEN_MODES.RESPONSE
    }

    if (props.toteStatus.wasCreatedBySameUser) {
      return SCREEN_MODES.AWAITING
    }

    return SCREEN_MODES.INITIAL
  }

  /**
   * @param {ToteEngagementMode} mode
   */
  function handleModeChange(mode) {
    const currentImage = props.getImageForMode(mode)
    setScreenState({
      ...screenState,
      mode: SCREEN_MODES.TEPLY,
      currentFlowMode: mode,
      currentImage
    })
    props.onReply({
      mode,
      teply: screenState.responseText,
      seenMeme: currentImage
    })
  }

  function getData() {
    const MODE_DATA = {
      [SCREEN_MODES.TEPLY]: {
        ...getTeplyProps(),
        mode: screenState.currentFlowMode,
        onSkip: () => {
          const currentImage = props.getImageForMode(
            screenState.currentFlowMode
          )
          setScreenState(s => ({
            ...s,
            mode: SCREEN_MODES.RESPONSE,
            currentImage
          }))
          props.onReply({
            seenMeme: currentImage,
            mode: screenState.currentFlowMode,
            teply: ''
          })
        },
        onSend: response => {
          const currentImage = props.getImageForMode(
            screenState.currentFlowMode
          )
          setScreenState(s => ({
            ...s,
            mode: SCREEN_MODES.RESPONSE,
            responseText: response,
            currentImage
          }))
          props.onReply({
            seenMeme: currentImage,
            mode: screenState.currentFlowMode,
            teply: response
          })
        }
      },
      [SCREEN_MODES.RESPONSE]: {
        mode: screenState.currentFlowMode,
        tesponse: screenState.responseText,
        studentName: props.toteStatus.studentName || 'Student',
        isTeacherView: props.toteStatus.wasCreatedBySameUser,
        onChangeResponse: () => {
          setScreenState({
            mode: SCREEN_MODES.INITIAL,
            responseText: '',
            currentFlowMode: '',
            currentImage: ''
          })
          props.onChangeResponse?.()
        }
      },
      [SCREEN_MODES.AWAITING]: {
        mode: SCREEN_MODES.AWAITING,
        isTeacherView: props.toteStatus.wasCreatedBySameUser,
        studentName: props.toteStatus.studentName || 'Student'
      }
    }
    return MODE_DATA[screenState.mode]
  }

  function getTeplyProps() {
    if (screenState.currentFlowMode === ToteEngagementModeEnum.AFFIRMATIVE) {
      return {
        title: `Let ${props.toteStatus.createdByName} know what you plan to do`,
        placeholder: 'I will...'
      }
    }
    return {
      title: `Let ${props.toteStatus.createdByName} know where you need help`,
      placeholder: 'I need help with...'
    }
  }

  function getChildren() {
    if (
      [SCREEN_MODES.INITIAL, SCREEN_MODES.AWAITING].includes(screenState.mode)
    ) {
      return props.children || null
    }
    return null
  }
}
